"use client";

import clsx from "clsx";
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type HTMLAttributes,
  type ReactNode,
} from "react";

/** Delay between consecutive items revealing. */
const STAGGER_MS = 120;

type RevealState = {
  revealed: boolean;
  reducedMotion: boolean;
};

// Items rendered outside a RevealSequence fall back to static rendering.
const RevealContext = createContext<RevealState>({
  revealed: true,
  reducedMotion: true,
});

function subscribeToReducedMotion(onChange: () => void) {
  const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  mediaQuery.addEventListener("change", onChange);
  return () => mediaQuery.removeEventListener("change", onChange);
}

function usePrefersReducedMotion() {
  return useSyncExternalStore(
    subscribeToReducedMotion,
    () => window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    () => false,
  );
}

export type RevealSequenceProps = HTMLAttributes<HTMLDivElement>;

/**
 * Container that reveals its `RevealItem` descendants with a staggered
 * pop-in once it scrolls into view. Each container observes itself, so
 * stacked cards (e.g. on mobile) animate one at a time as they appear.
 * With reduced motion, everything renders statically.
 */
export function RevealSequence({ children, ...rest }: RevealSequenceProps) {
  const [inView, setInView] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const reducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.35 },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} {...rest}>
      <RevealContext.Provider value={{ revealed: inView, reducedMotion }}>
        {children}
      </RevealContext.Provider>
    </div>
  );
}

export type RevealItemProps = {
  /** Position in the reveal order; the delay is `order * 120ms`. */
  order: number;
  children: ReactNode;
  className?: string;
};

/**
 * A row inside a RevealSequence. Keeps its layout space reserved while
 * hidden so the frame never reflows, then pops in at its turn.
 */
export function RevealItem({
  order,
  children,
  className,
}: Readonly<RevealItemProps>) {
  const { revealed, reducedMotion } = useContext(RevealContext);

  if (reducedMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div
      className={clsx(
        className,
        revealed ? "animate-message-in" : "invisible",
      )}
      style={revealed ? { animationDelay: `${order * STAGGER_MS}ms` } : undefined}
    >
      {children}
    </div>
  );
}
